import { useCallback, useEffect, useState } from 'react'

import { BrowserStorage } from 'UTILS/browserStorage'

const PENDING_TOPUP_KEY = 'credits.pending-topup'
const POLL_INTERVAL_MS = 2000
const MAX_POLLS = 5

const storage = new BrowserStorage(window.sessionStorage)

interface PendingTopup {
  balanceBefore: number | null
}

interface UseBalanceAfterPurchaseResult {
  markPurchaseStarted: () => void
  cancelPurchase: () => void
}

export const useBalanceAfterPurchase = ({
  balance,
  isCheckoutOpen,
  refetchBalance,
}: {
  balance?: number
  isCheckoutOpen: boolean
  refetchBalance: () => unknown
}): UseBalanceAfterPurchaseResult => {
  const [pending, setPending] = useState<PendingTopup | null>(() =>
    storage.getItem<PendingTopup>(PENDING_TOPUP_KEY),
  )
  const [polls, setPolls] = useState(0)

  const cancelPurchase = useCallback((): void => {
    storage.removeItem(PENDING_TOPUP_KEY)
    setPending(null)
    setPolls(0)
  }, [])

  const markPurchaseStarted = useCallback((): void => {
    const topup = { balanceBefore: balance ?? null }

    storage.setItem(PENDING_TOPUP_KEY, topup)
    setPending(topup)
    setPolls(0)
  }, [balance])

  useEffect(() => {
    if (pending === null || isCheckoutOpen) return

    const hasCreditsArrived =
      balance !== undefined && pending.balanceBefore !== null && balance !== pending.balanceBefore

    if (hasCreditsArrived || polls >= MAX_POLLS) {
      cancelPurchase()
      return
    }

    const timeout = setTimeout(() => {
      setPolls((count) => count + 1)
      void refetchBalance()
    }, POLL_INTERVAL_MS)

    return () => {
      clearTimeout(timeout)
    }
  }, [pending, isCheckoutOpen, balance, polls, refetchBalance, cancelPurchase])

  return { markPurchaseStarted, cancelPurchase }
}
